import { Component, Input, OnChanges } from '@angular/core';
import { saveFileAs } from '@scripts/utils/files';
import { SimpleChanges } from '@scripts/utils/types';
import { AuthService } from '@scripts/woo_services.module/AuthService';
import { CampaignService } from '@scripts/woo_services.module/CampaignService';
import { Campaign, CampaignStatus } from '@scripts/woo_services.module/shared-types';

@Component({
  selector: 'manage-campaign-buttons',
  templateUrl: './manage-campaign-buttons.component.html',
  standalone: false,
})
export class ManageCampaignButtons implements OnChanges {
  @Input() campaign: Campaign;
  @Input() campaignStartsToday: boolean;

  show = {
    cancel: false,
    close: false,
    edit: false,
    duplicate: false,
    remove: false,
    review: false,
    sendToAdServer: false,
    stop: false,
    proposalDownload: false,
  };

  disable = {
    cancel: false,
    close: false,
    edit: false,
    duplicate: false,
    remove: false,
    review: false,
    sendToAdServer: false,
    stop: false,
    proposalDownload: false,
  };

  constructor(private authService: AuthService, private campaignService: CampaignService) {}

  ngOnChanges(changes: SimpleChanges<ManageCampaignButtons>): void {
    if (changes.campaign && this.campaign) {
      if (this.campaign.low_credit_rating !== undefined) {
        // Show buttons based on status
        this.hideAllButtons();
        switch (this.campaign.status) {
          case CampaignStatus.unbooked:
            this.show.duplicate = this.authService.allowedToBook();
            this.show.remove = true;
            this.show.proposalDownload = true;
            break;

          case CampaignStatus.booked:
            this.show.cancel = this.authService.allowedToBookEarlyWithoutVerifyCapeIoCode();
            this.show.duplicate = this.authService.allowedToBook();
            this.show.sendToAdServer = this.authService.adminOrPlanner();
            if (this.campaign.cash_campaign) {
              this.show.review = this.authService.adminOrPlanner();
            }
            break;

          case CampaignStatus.upcoming:
            this.show.cancel = this.authService.allowedToBookEarlyWithoutVerifyCapeIoCode();
            this.show.duplicate = this.authService.allowedToBook();
            this.show.sendToAdServer = this.authService.adminOrPlanner();
            break;

          case CampaignStatus.ongoing:
            this.show.duplicate = this.authService.allowedToBook();
            this.show.stop = this.authService.adminOrPlanner();
            if (this.campaignStartsToday && !this.campaign.enabled) {
              this.show.sendToAdServer = this.authService.adminOrPlanner();
            }
            break;

          case CampaignStatus.ended:
            this.show.close = this.authService.adminPlannerOrAccounting();
            this.show.duplicate = this.authService.allowedToBook();
            this.show.remove = this.authService.adminOrPlanner();
            break;

          case CampaignStatus.cancelled:
          // fallthrough
          case CampaignStatus.stopped:
            this.show.close = this.authService.adminPlannerOrAccounting();
            this.show.duplicate = this.authService.allowedToBook();
            this.show.remove = this.authService.adminOrPlanner();
            break;

          case CampaignStatus.reviewing:
          // fallthrough
          case CampaignStatus.rejected:
          // fallthrough
          case CampaignStatus.closed:
            this.show.duplicate = this.authService.allowedToBook();
            break;

          default:
            this.hideAllButtons();
        }

        const editPermission = this.campaignService.getEditPermission(this.campaign);
        this.show.edit = editPermission.visible;

        // Disable buttons based on state
        this.disable.duplicate = this.campaign.ttv_campaign;
        this.disable.edit = editPermission.disabled;
        this.disable.sendToAdServer = this.campaign.inventory_only_campaign;
      }
    }
  }

  private hideAllButtons = () => {
    for (const key in this.show) {
      if (this.show.hasOwnProperty(key)) {
        this.show[key] = false;
      }
    }
  };

  // Move to its own button class
  download = (): void => {
    const campaign = this.campaign;
    this.campaignService.downloadProposalExcel(campaign).then((res) => {
      saveFileAs(res.body, `Offert - ${campaign.name}.xlsx`);
    });
  };
}
